import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Box, TextField, Button, Typography, Alert, CircularProgress } from '@mui/material';
import { validateCoupon, clearValidatedCoupon } from '../../store/slices/couponSlice';
import { updateCheckoutField } from '../../store/slices/checkoutSlice';
import { useCart } from '../../contexts/CartContext';

/**
 * Component for coupon code input and validation in checkout
 * Allows users to enter and apply coupon codes during checkout
 * @returns {JSX.Element} The coupon input component
 */
const CouponInput = () => {
  const { t } = useTranslation();
  const { t: tCoupon } = useTranslation('coupon');
  const dispatch = useDispatch();
  const { cart, applyCoupon, removeCoupon, loading: cartLoading } = useCart();

  // Get coupon state from Redux
  const { validationStatus, validationError, validatedCoupon } = useSelector(
    (state) => state.coupons,
  );
  const { couponCode } = useSelector((state) => state.checkout.checkoutData);

  // Local state for coupon input and validation message
  const [localCouponCode, setLocalCouponCode] = useState(couponCode || '');
  const [applicationError, setApplicationError] = useState(null);
  const [couponApplied, setCouponApplied] = useState(false);

  // Detect if a coupon is already applied in the cart
  useEffect(() => {
    setCouponApplied(cart && cart.discount > 0 && cart.couponCode);
  }, [cart]);

  // Handle coupon code change
  const handleCouponChange = (e) => {
    setLocalCouponCode(e.target.value.toUpperCase());
    setApplicationError(null);

    // Clear validation state when typing
    if (validationError || validatedCoupon) {
      dispatch(clearValidatedCoupon());
    }
  };

  // Handle apply coupon button click
  const handleApplyCoupon = async () => {
    if (!localCouponCode) return;

    try {
      // First validate the coupon with the coupon service
      const validationData = {
        code: localCouponCode,
        cartTotal: cart.subtotal || 0,
        cartItems: cart.items || [],
      };

      // Dispatch validation and wait for result
      const validationResult = await dispatch(validateCoupon(validationData)).unwrap();

      if (validationResult && validationResult.isValid) {
        console.log('Coupon validated successfully, applying to cart:', validationResult);

        // Now apply the coupon to the cart
        const updatedCart = await applyCoupon(localCouponCode);

        if (updatedCart) {
          // Update checkout data with coupon code
          dispatch(updateCheckoutField({ field: 'couponCode', value: localCouponCode }));
          setApplicationError(null);
          setCouponApplied(true);
        }
      } else {
        // This shouldn't happen as validation errors should be caught in the catch block
        setApplicationError(tCoupon('checkout.couponValidationFailed'));
      }
    } catch (error) {
      console.error('Failed to apply coupon:', error);
      // Extract the error message from the network response
      // First try to get the direct error message from response data
      const backendMessage =
        error?.response?.data?.message || // From axios response
        error?.payload?.message || // From Redux error payload
        error?.message || // Direct error message
        tCoupon('checkout.couponApplyFailed'); // Fallback

      setApplicationError(backendMessage);
    }
  };

  // Handle removing coupon
  const handleRemoveCoupon = async () => {
    try {
      await removeCoupon();
      setLocalCouponCode('');
      dispatch(updateCheckoutField({ field: 'couponCode', value: '' }));
      dispatch(clearValidatedCoupon());
      setApplicationError(null);
      setCouponApplied(false);
    } catch (error) {
      console.error('Failed to remove coupon:', error);
      setApplicationError(error.message || tCoupon('checkout.couponRemoveFailed'));
    }
  };

  // Determine loading state
  const isLoading = validationStatus === 'loading' || cartLoading;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography
        variant="h6"
        gutterBottom
      >
        {tCoupon('checkout.couponCode')}
      </Typography>

      {couponApplied && cart.couponCode && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleRemoveCoupon}
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress
                  size={16}
                  color="inherit"
                />
              ) : (
                tCoupon('checkout.removeCoupon')
              )}
            </Button>
          }
        >
          {tCoupon('checkout.couponApplied')} <strong>{cart.couponCode}</strong>
          <Typography variant="body2">
            {tCoupon('checkout.couponDiscountAmount', {
              amount: typeof cart.discount === 'number' ? cart.discount.toFixed(2) : 0.0,
            })}
          </Typography>
        </Alert>
      )}

      {(validationError || applicationError) && !couponApplied && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
        >
          {applicationError ||
            tCoupon(`checkout.couponError.${validationError}`) ||
            tCoupon('checkout.couponInvalid')}
        </Alert>
      )}

      {!couponApplied && (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            id="couponCode"
            label={tCoupon('checkout.couponCodeLabel')}
            value={localCouponCode}
            onChange={handleCouponChange}
            placeholder={tCoupon('checkout.enterCouponCode')}
            sx={{ flexGrow: 1 }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleApplyCoupon}
            disabled={isLoading || !localCouponCode}
            sx={{ minWidth: '100px' }}
          >
            {isLoading ? (
              <CircularProgress
                size={24}
                color="inherit"
              />
            ) : (
              tCoupon('checkout.applyCoupon')
            )}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default CouponInput;
